import {useMemo} from 'react';

import type {TrustAccountFilterFields} from '../config';
import type {TrustAccountFilterState} from '../utils/filter.utils';
import type {TrustPurpose, TrustStatus} from '@sollapay/enums';

export interface FilterableTrustAccount {
  refId: string;
  trustStatus: TrustStatus;
  trustPurpose?: TrustPurpose | null;
}

export function useFilteredTrustAccounts<T extends FilterableTrustAccount>(
  accounts: T[],
  searchText: string,
  filter: TrustAccountFilterState,
  fields: TrustAccountFilterFields<T>
): T[] {
  return useMemo(() => {
    let result = accounts;

    if (filter.statuses.length > 0) {
      result = result.filter(a => filter.statuses.includes(a.trustStatus));
    }

    if (filter.purposes.length > 0) {
      result = result.filter(
        a => a.trustPurpose != null && filter.purposes.includes(a.trustPurpose)
      );
    }

    if (filter.dateRange) {
      const {from, to} = filter.dateRange;
      const toEndOfDay = new Date(to);
      toEndOfDay.setHours(23, 59, 59, 999);
      result = result.filter(a => {
        const date = new Date(fields.getDate(a));
        return date >= from && date <= toEndOfDay;
      });
    } else if (filter.createdAt !== 'all') {
      const days = filter.createdAt === '7d' ? 7 : filter.createdAt === '30d' ? 30 : 90;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      result = result.filter(a => new Date(fields.getDate(a)) >= cutoff);
    }

    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      const queryDigits = accountNumberDigits(query);
      result = result.filter(
        a =>
          fields.getName(a).toLowerCase().includes(query) ||
          (queryDigits.length >= MIN_ACCOUNT_NUMBER_QUERY_DIGITS &&
            accountNumberDigits(a.refId).includes(queryDigits))
      );
    }

    return result;
  }, [accounts, searchText, filter, fields]);
}

const MIN_ACCOUNT_NUMBER_QUERY_DIGITS = 3;
const ACCOUNT_NUMBER_SUFFIX = /(\d{3})-?(\d{3})$/;

/** The six digits after the firm prefix — the prefix is shared by the whole firm and may itself hold digits. */
function accountNumberDigits(value: string): string {
  const suffix = ACCOUNT_NUMBER_SUFFIX.exec(value);
  return suffix ? `${suffix[1]}${suffix[2]}` : value.replace(/\D/g, '');
}
